import PropTypes from "prop-types";
import React from "react";
import Modal from "../../Modal";
import { Typography, Button, TextField } from "@mui/material";
import EventIcon from "@mui/icons-material/Event";
import makeStyles from "@mui/styles/makeStyles";
import { validateUrl } from "../../../utils/validation";
import TextFieldUploadButton from "../../Buttons/TextFieldUploadButton";
import { useTranslation } from "react-i18next";

const placeholderUrl = "https://";

const useStyles = makeStyles(theme => ({
  subtitle: {
    marginBottom: theme.spacing(2)
  },
  divider: {
    textAlign: "center",
    marginTop: theme.spacing(4),
    marginBottom: theme.spacing(2)
  }
}));

const ImportCalendar = ({ onOpenChange, onSubmit, open }) => {
  const classes = useStyles();
  const { t } = useTranslation();
  const [importUrlFieldDisabled, setUrlImportUrlFieldDisabled] = React.useState(
    false
  );
  const [importFileDisabled, setImportFileDisabled] = React.useState(false);
  const [importUrlNotValid, setImportUrlNotValid] = React.useState(false);
  const [importedUrlValue, setImportUrlValue] = React.useState(placeholderUrl);
  const [importedFile, setImportedFile] = React.useState(null);

  const handleImportUrlChange = newUrl => {
    if (importUrlNotValid && validateUrl(newUrl)) {
      setImportUrlNotValid(false);
    }
    setImportFileDisabled(!(newUrl === "" || newUrl === placeholderUrl));
    setImportUrlValue(newUrl);
  };

  const handleImportedFileChange = newFile => {
    setImportedFile(newFile);
    setUrlImportUrlFieldDisabled(!!newFile);
  };

  const handleSubmit = () => {
    if (!importedFile && !validateUrl(importedUrlValue)) {
      setImportUrlNotValid(true);
    } else {
      onSubmit({ importedFile, importedUrlValue });
    }
  };

  return (
    <Modal
      showCloseIcon
      dialogTitle={
        <Typography variant="h4" color="textPrimary">
          {t("importCalendar.title")}
        </Typography>
      }
      open={open}
      onClose={() => onOpenChange(false)}
      dialogContent={
        <>
          <Typography
            className={classes.subtitle}
            variant="h5"
            color="textSecondary"
          >
            {t("importCalendar.fromUrlLabel")}
          </Typography>
          <TextField
            error={importUrlNotValid}
            helperText={
              importUrlNotValid && t("importCalendar.invalidUrlHelperText")
            }
            disabled={importUrlFieldDisabled}
            variant="filled"
            fullWidth
            multiline
            value={importedUrlValue}
            onChange={ev => {
              handleImportUrlChange(ev.target.value);
            }}
            hiddenLabel
          />
          <Typography
            className={classes.divider}
            variant="h5"
            color="textSecondary"
          >
            {t("importCalendar.orOptionMsg")}
          </Typography>
          <Typography
            className={classes.subtitle}
            variant="h5"
            color="textSecondary"
          >
            {t("importCalendar.byFileLabel")}
          </Typography>
          <TextFieldUploadButton
            acceptType=".ical,.ics, .csv"
            helperText={t("importCalendar.uploadFileHelperText")}
            icon={<EventIcon />}
            onFileUpload={handleImportedFileChange}
            file={importedFile}
            disabled={importFileDisabled}
          />
        </>
      }
      dialogActions={
        <>
          <Button
            color="primary"
            onClick={() => {
              onOpenChange(false);
            }}
          >
            {t("importCalendar.cancelBtn")}
          </Button>
          <Button
            disabled={!importFileDisabled && !importUrlFieldDisabled}
            color="primary"
            variant="contained"
            onClick={handleSubmit}
          >
            {t("importCalendar.importBtn")}
          </Button>
        </>
      }
      maxWidth="sm"
      fullWidth
      scrollable
    />
  );
};

ImportCalendar.propTypes = {
  /**
   * True if the import modal is open, false otherwise
   */
  open: PropTypes.bool,
  /**
   * Called when the import modal is open or closed
   */
  onOpenChange: PropTypes.func,
  /**
   * Called when the user presses the Import button to import a new calendar
   */
  onSubmit: PropTypes.func
};

export default ImportCalendar;
